import { Box, Card, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton, TextField, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isHttpStatusOk } from '../../generic/Api_Functions'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

const RuleTester = ({ rule, isRuleValid, setRuleTestResult, setRuleTestOpen }) => {
  const { t_ } = useSmartTranslation()

  const [testValues, setTestValues] = useState({})
  const [result, setResult] = useState(null)

  const getElementFakeId = useCallback((element) => {
    return element.type + '_' + element.id
  }, [])

  const getElementTestValue = useCallback((element) => {
    const fakeId = getElementFakeId(element)
    if (fakeId in testValues) {
      return testValues[fakeId]
    }
    if (element.type === 'rule') {
      return false
    }
    return 0
  }, [getElementFakeId, testValues])

  const addIfNotFound = useCallback((variables, newItem) => {
    const array = newItem.type === 'rule' ? variables.rules : variables.formulas
    const oldItem = array.find((item) => item.type === newItem.type && item.id === newItem.id)
    if (!oldItem) {
      array.push(newItem)
    }
  }, [])

  const checkVariables = useCallback((variables, arrayToCheck) => {
    arrayToCheck.forEach(element => {
      if ('all' in element) {
        checkVariables(variables, element.all)
        return
      }
      if ('any' in element) {
        checkVariables(variables, element.any)
        return
      }
      if (element.fact) {
        addIfNotFound(variables, element.fact)
      }
      if (element.value && element.value !== true) {
        addIfNotFound(variables, element.value)
      }
    })
  }, [addIfNotFound])

  const variables = useMemo(() => {
    const variables = { rules: [], formulas: [] }
    if (!rule) {
      return variables
    }
    for (const key in rule) {
      checkVariables(variables, rule[key])
    }
    return variables
  }, [rule, checkVariables])

  const replaceFactsAndValues = useCallback((element) => {
    if ('all' in element) {
      element.all.forEach((item) => replaceFactsAndValues(item))
      return
    }
    if ('any' in element) {
      element.any.forEach((item) => replaceFactsAndValues(item))
      return
    }
    if (element.fact) {
      element.fact = getElementFakeId(element.fact)
    }
    if (element.value && typeof element.value === 'object') {
      element.value = getElementTestValue(element.value)
    }
  }, [getElementFakeId, getElementTestValue])

  useEffect(() => {
    setResult(null)
    const getResult = setTimeout(async () => {
      if (!rule) {
        return
      }
      const data = structuredClone(rule)
      replaceFactsAndValues(data)
      const facts = {}
      variables.formulas.forEach((variable) => { facts[getElementFakeId(variable)] = getElementTestValue(variable) })
      variables.rules.forEach((variable) => { facts[getElementFakeId(variable)] = getElementTestValue(variable) })
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ conditions: data, facts })
      }
      const response = await fetch('/rule-evaluator/evaluate', options)
      if (isHttpStatusOk(response.status)) {
        const responseData = await response.json()
        setResult(responseData)
        setRuleTestResult(!responseData.error ? responseData.result : null)
        return
      }
      setResult({ error: true })
    }, 300)
    return () => { clearTimeout(getResult) }
  }, [rule, replaceFactsAndValues, getElementFakeId, getElementTestValue, variables, setRuleTestResult])

  const getResultIndicator = () => {
    if (!result) {
      return <Box sx={{ height: '6rem' }}><CircularProgress /></Box>
    }
    if (result.error) {
      return t_('Error evaluating rule')
    }
    const iconStyle = { width: '100%', height: '100%' }
    return (
      <div style={{ maxHeight: '6rem', padding: '1.2rem', aspectRatio: '1/1', backgroundColor: result.result.result ? 'green' : 'red', borderRadius: '50%' }}>{result.result.result ? <CheckIcon sx={iconStyle} /> : <CloseIcon sx={iconStyle} />}</div>
    )
  }

  return (
    <Card style={{ height: '100%' }}>

      <Stack direction='column' sx={{ overflow: 'auto', height: '100%', padding: '1rem' }} gap={3}>

        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='h5'>{t_('Test rule')}</Typography>
          <IconButton onClick={() => setRuleTestOpen(false)}><CloseIcon /></IconButton>
        </Stack>

        {isRuleValid

          ? (
            <>
              <Stack gap={2}>
                {variables.formulas.length > 0 ? <Typography color='primary'>{t_('Formulas')}</Typography> : null}
                {variables.formulas.map((variable) => {
                  return (
                    <TextField
                      key={getElementFakeId(variable)}
                      label={variable.name} inputProps={{ type: 'number' }}
                      value={getElementTestValue(variable)}
                      onChange={(event) => {
                        const newTestValues = { ...testValues }
                        newTestValues[getElementFakeId(variable)] = parseFloat(event.target.value)
                        setTestValues(newTestValues)
                      }}
                    />
                  )
                })}
              </Stack>
              <Stack gap={2}>
                {variables.rules.length > 0 ? <Typography color='primary'>{t_('Rules')}</Typography> : null}
                {variables.rules.map((variable) => {
                  return (
                    <FormControlLabel
                      key={getElementFakeId(variable)}
                      control={<Checkbox
                        checked={getElementTestValue(variable)}
                        onChange={(event) => {
                          const newTestValues = { ...testValues }
                          newTestValues[getElementFakeId(variable)] = event.target.checked
                          setTestValues(newTestValues)
                        }}
                               />} label={variable.name}
                    />

                  )
                })}
              </Stack>
              <Divider orientation='horizontal' />
              <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{getResultIndicator()}</div>
            </>
            )
          : <Typography color='error'>{t_('Invalid rule')}</Typography>}

      </Stack>
    </Card>
  )
}

export default RuleTester
